<template>
  <div
    v-if="isShowing"
    ref="interactElement"
    :class="{ isAnimating: isInteractAnimating, isCurrent: isCurrent }"
    class="card"
    :style="{ transform: transformString }"
  >
    <h3 class="cardTitle">
      <div v-if="isBack">🂠</div>
      <div v-if="!isBack">
        {{ myCard }}
        <div class="rule" v-show="isCurrent">
          <div style="font-size:1.5rem;margin-bottom:10px">{{ rule.name }}</div>
          <div>{{ rule.desc }}</div>
        </div>
      </div>
    </h3>
  </div>
</template>

<script>
import interact from "interact.js";
const ACCEPT_CARD = "cardAccepted";
const REJECT_CARD = "cardRejected";
const SKIP_CARD = "cardSkipped";

export default {
  name: "Card",
  static: {
    interactMaxRotation: 15,
    interactOutOfSightXCoordinate: 500,
    interactOutOfSightYCoordinate: 600,
    interactYThreshold: 150,
    interactXThreshold: 100
  },
  props: {
    card: {
      type: Object,
      required: true
    },
    isCurrent: {
      type: Boolean,
      required: true
    },
    isBack: {
      type: Boolean,
      required: true
    },
    canInteract: {
      type: Boolean,
      required: false,
      default: true
    }
  },
  data() {
    return {
      isShowing: true,
      isInteractAnimating: true,
      isInteractDragged: null,
      interactPosition: {
        x: 0,
        y: 0,
        rotation: 0
      },
      cards: [
        //spade
        ["🂡", "🂢", "🂣", "🂤", "🂥", "🂦", "🂧", "🂨", "🂩", "🂪", "🂫", "🂭", "🂮"],
        //hearts
        ["🂱", "🂲", "🂳", "🂴", "🂵", "🂶", "🂷", "🂸", "🂹", "🂺", "🂻", "🂽", "🂾"],
        //diamond
        ["🃁", "🃂", "🃃", "🃄", "🃅", "🃆", "🃇", "🃈", "🃉", "🃊", "🃋", "🃍", "🃎"],
        //clubs
        ["🃑", "🃒", "🃓", "🃔", "🃕", "🃖", "🃗", "🃘", "🃙", "🃚", "🃛", "🃝", "🃞"]
      ],
      rules: {
        A: {
          name: "Waterfall",
          desc:
            "Each player starts drinking at the same time as the person to their left. NO player can stop drinking until the person before them stops."
        },
        2: {
          name: "You",
          desc: "Whoever draws this can choose anyone to take a drink."
        },
        3: {
          name: "Me",
          desc: "The person who draws this takes a drink."
        },
        4: {
          name: "Floor",
          desc: "The last person to touch the floor takes a drink."
        },
        5: {
          name: "Guys",
          desc: "All the guys at the table drink."
        },
        6: {
          name: "Chicks",
          desc: "All the girls at the table drink."
        },
        7: {
          name: "Heaven",
          desc:
            "If you draw this card, raise your hand above you head. Every other player must do so as well. The last person takes a drink."
        },
        8: {
          name: "Mate",
          desc:
            "Choose someone to be your mate. For the rest of the game, they drink when you drink."
        },
        9: {
          name: "Rhyme",
          desc:
            "You say a word, then the person to your right has to say a word that rhymes. This continues until someone can't think of a word. That person takes a drink. You can't reuse words."
        },
        10: {
          name: "Categories",
          desc:
            "Come up with a category of things. The person to your right must name something that falls within the category. This continues until someone can't think of something. That person takes a drink"
        },
        J: {
          name: "Never Have I Ever",
          desc: "Never Have I Ever."
        },
        Q: {
          name: "Questions",
          desc:
            "Ask someone a question. That person then asks someone else a question. This continues until someone messes up or fails to ask a question. That person drinks. No question can be repeated."
        },
        K: {
          name: "King's Cup",
          desc:
            "Set a rule to be followed, e.g. stand on one foot when you drink, only speak in an accent, etc."
        }
      }
    };
  },
  computed: {
    rule() {
      const { rank } = this.card;
      if (rank) {
        return this.rules[rank];
      }
      return "";
    },
    myCard() {
      const { rank, suit } = this.card;
      let faceIndexMap = { A: 0, J: 10, Q: 11, K: 12 };
      let rankIndex = !isNaN(rank) ? parseInt(rank) - 1 : faceIndexMap[rank];
      const suitIndexMap = { Spades: 0, Hearts: 1, Clubs: 2, Diamonds: 3 };
      let suitIndex = suitIndexMap[suit];
      if (suitIndex != undefined && rankIndex != undefined) {
        return this.cards[suitIndex][rankIndex];
      }
      return "🃟";
    },
    transformString() {
      if (!this.isInteractAnimating || this.isInteractDragged) {
        const { x, y, rotation } = this.interactPosition;
        return `translate3D(${x}px, ${y}px, 0) rotate(${rotation}deg)`;
      }
      return null;
    }
  },
  methods: {
    hideCard() {
      setTimeout(() => {
        this.isShowing = false;
        this.$emit("hideCard", this.card);
      }, 300);
    },
    playCard(interaction) {
      const {
        interactOutOfSightXCoordinate,
        interactOutOfSightYCoordinate,
        interactMaxRotation
      } = this.$options.static;

      this.interactUnsetElement();

      switch (interaction) {
        case ACCEPT_CARD:
          this.interactSetPosition({
            x: interactOutOfSightXCoordinate,
            rotation: interactMaxRotation
          });
          this.$emit(ACCEPT_CARD);
          break;
        case REJECT_CARD:
          this.interactSetPosition({
            x: -interactOutOfSightXCoordinate,
            rotation: -interactMaxRotation
          });
          this.$emit(REJECT_CARD);
          break;
        case SKIP_CARD:
          this.interactSetPosition({
            y: interactOutOfSightYCoordinate
          });
          this.$emit(SKIP_CARD);
          break;
      }

      this.hideCard();
    },
    interactSetPosition(coordinates) {
      const { x = 0, y = 0, rotation = 0 } = coordinates;
      this.interactPosition = { x, y, rotation };
    },
    interactUnsetElement() {
      interact(this.$refs.interactElement).unset();
      this.isInteractDragged = true;
    },
    resetCardPosition() {
      this.interactSetPosition({ x: 0, y: 0, rotation: 0 });
    },
    onstart() {
      this.isInteractAnimating = false;
    },
    onmove(event) {
      const { interactMaxRotation, interactXThreshold } = this.$options.static;
      const x = this.interactPosition.x + event.dx;
      const y = this.interactPosition.y + event.dy;
      let rotation = interactMaxRotation * (x / interactXThreshold);
      if (rotation > interactMaxRotation) {
        rotation = interactMaxRotation;
      } else if (rotation < -interactMaxRotation) {
        rotation = -interactMaxRotation;
      }
      this.interactSetPosition({ x, y, rotation });
    },
    onend() {
      const { x, y } = this.interactPosition;
      const { interactXThreshold, interactYThreshold } = this.$options.static;
      this.isInteractAnimating = true;
      if (x > interactXThreshold) {
        this.playCard(ACCEPT_CARD);
      } else if (x < -interactXThreshold) {
        this.playCard(REJECT_CARD);
      } else if (y > interactYThreshold) {
        this.playCard(SKIP_CARD);
      } else {
        this.resetCardPosition();
      }
    }
  },
  mounted() {
    const element = this.$refs.interactElement;
    const { onstart, onmove, onend } = this;
    if (this.canInteract) {
      interact(element).draggable({ onstart, onmove, onend });
    }
  },
  beforeDestroy() {
    interact(this.$refs.interactElement).unset();
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/css/index.scss";

$cardsTotal: 3;
$cardsWidth: 300px;
$cardsPositionOffset: 55vh * 0.06;
$cardsScaleOffset: 0.08;
$defaultTranslation: $cardsPositionOffset * $cardsTotal;
$defaultScale: 1 - ($cardsScaleOffset * $cardsTotal);
$fs-card-title: 1.125em;

.card {
  @include card();
  @include absolute(0);
  @include sizing(100% 80vw);
  @include flex-center();

  @include after() {
    @include sizing(21px 3px);
    @include absolute(right 0 bottom 11px left 0);

    margin: auto;
    border-radius: 100px;
    background: rgba($c-black, 0.3);
  }

  display: flex;
  max-height: 350px;
  margin: auto;
  font-size: $fs-h2;
  font-weight: $fw-bold;
  color: $c-white;
  background-image: linear-gradient(
    -180deg,
    $primary-gradient-start 2%,
    $primary-gradient-end 100%
  );
  opacity: 0;
  transform: translateY($defaultTranslation) scale($defaultScale);
  transform-origin: 50%, 100%;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  user-select: none;
  pointer-events: none;
  will-change: transform, opacity;

  height: 100vw;

  &.isCurrent {
    pointer-events: auto;
  }

  &.isAnimating {
    transition: transform 0.7s $ease-out-back;
  }
}

.cardTitle {
  margin: 0 0 15px;
  font-size: $fs-card-title;
  font-size: 22rem;
  margin-bottom: 55px;
}

@for $i from 1 through $cardsTotal {
  $index: $i - 1;
  $translation: $cardsPositionOffset * $index;
  $scale: 1 - ($cardsScaleOffset * $index);

  .card:nth-child(#{$i}) {
    z-index: $cardsTotal - $index;
    opacity: 1;
    transform: translateY($translation) scale($scale);

    @if $i == 3 {
      color: $c-red-25;
      background-color: $c-red-25;
    } @else if $i == 2 {
      color: $c-red-50;
      background-color: $c-red-50;
    }

    @if $i != 1 {
      background-image: none;

      @include after() {
        @include sizing(0 0);
      }
    }
  }
  .rule {
    font-size: 1rem;
    color: white;
    backdrop-filter: blur(15px);
    text-shadow: 1px 1px 0 rgba(0, 0, 0, 0.35);
    position: absolute;
    bottom: 25px;
    text-align: left;
    padding: 15px;
  }
}
</style>
